import { PageRenderer, RenderedPage, fetchFile, fitFactor } from './page-renderer';

/**
 * Word (`.docx`) via docx-preview — the document laid out as HTML pages (`section.docx`), split at
 * the page breaks Word recorded.
 *
 * Close to Word, not pixel-perfect: complex floating layouts can shift. The legacy binary `.doc`
 * is not supported by the library; the viewer never routes one here.
 *
 * Zoom applies CSS `zoom` to the wrapper, which reflows the layout box so the pane scrolls
 * correctly. Pins are stored relative to each page, so they stay aligned at any zoom.
 */
export class DocxRenderer implements PageRenderer {

    readonly paged: boolean = false;
    readonly hasText: boolean = true;

    private host: HTMLElement | null = null;
    private body: HTMLElement | null = null;
    private naturalWidth: number = 0;

    async render(src: string, host: HTMLElement): Promise<RenderedPage[]> {
        this.host = host;
        const { renderAsync } = await import('docx-preview');
        const data: ArrayBuffer = await fetchFile(src);

        // Styles are written next to the body, so they are removed with it.
        const styles: HTMLDivElement = document.createElement('div');
        const body: HTMLDivElement = document.createElement('div');
        body.className = 'pv-docx';
        host.append(styles, body);
        this.body = body;

        await renderAsync(data, body, styles, {
            className: 'docx',
            inWrapper: true,
            breakPages: true,
            ignoreLastRenderedPageBreak: false,
            renderHeaders: true,
            renderFooters: true,
            useBase64URL: true
        });

        const sections: HTMLElement[] = Array.from(body.querySelectorAll<HTMLElement>('section.docx'));
        sections.forEach((section) => section.classList.add('pv-page', 'is-docx'));
        this.naturalWidth = sections[0]?.offsetWidth ?? 0;

        await this.zoom(100);
        return sections.map((el, index) => ({ el, label: `Page ${index + 1}` }));
    }

    async zoom(zoom: number): Promise<void> {
        if (!this.host || !this.body) {
            return;
        }
        this.body.style.setProperty('zoom', String(fitFactor(this.host, this.naturalWidth) * (zoom / 100)));
    }

    destroy(): void {
        this.body = null;
        this.host = null;
    }
}
